import { useEffect, useMemo, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../auth/AuthProvider.jsx";
import { canCheckIn } from "../lib/roles.js";
import { useToaster } from "../components/Toaster.jsx";
import { BatteryGlyph, ChargingHalo, LiveTick } from "../components/BatteryViz.jsx";
import { useActiveStore } from "../lib/activeStore.js";
import {
  subscribeUnits, recordQuickCheck, authorFromProfile,
  chargeCycleStatus, daysSinceCheck, elapsedCharging,
  setStickyNote, relativeTime, CHARGE_CYCLE,
} from "../lib/data.js";

// The lot tech's daily walk. Mobile-first card list, charge-cycle-bucketed.
// Strictly time-based: a unit shows up here when its charge cycle is
// approaching the threshold (28d) or has crossed it (35d). Voltage is
// captured during the check-in form but is NOT part of the queue logic —
// the user doesn't drive priorities off voltage readings here. Same
// 28/35-day thresholds as the legacy BatteryCareTracker_v8 app.
export function Inventory() {
  const { profile } = useAuth();
  const navigate = useNavigate();
  const [units, setUnits] = useState([]);
  const [error, setError] = useState(null);
  const [pendingId, setPendingId] = useState(null); // unit id currently mid-write
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all"); // all | overdue | due | charging
  const [selected, setSelected] = useState(() => new Set()); // bulk-select unit ids
  const [bulkPending, setBulkPending] = useState(false);
  const { toast } = useToaster();

  const activeStore = useActiveStore();
  useEffect(() => {
    if (!profile?.tenantId || !activeStore) return;
    return subscribeUnits(
      { tenantId: profile.tenantId, storeId: activeStore },
      (u, err) => { if (err) setError(err); else { setUnits(u); setError(null); } },
    );
  }, [profile?.tenantId, activeStore]);

  const writable = canCheckIn(profile);

  // Search + filter. Search hits stock # / model / customer / year — same
  // fields a lot tech sees on the card. Filter narrows the visible buckets.
  // Both apply BEFORE bucketing so the counts in the chips reflect the
  // filtered (not pre-search) state.
  const visibleUnits = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return units;
    return units.filter((u) => {
      const hay = [
        u.stockNumber, u.model, u.customer,
        u.year, u.make, u.trim, u.chassis, u.lotPos,
      ].filter(Boolean).join(" ").toLowerCase();
      return hay.includes(q);
    });
  }, [units, search]);

  // Charge-cycle buckets — built off the search-filtered list. Charging
  // units pin to the top of the queue so the tech sees in-progress work
  // first (and can flip them to "Done" once the charger is unplugged) —
  // matches legacy v8 column 1 behavior (renderTVMode line 2429: "show
  // charging in first column too"). The filter pill narrows which buckets
  // we actually render.
  const buckets = useMemo(() => {
    const charging = [];
    const overdue  = [];
    const due      = [];
    for (const u of visibleUnits) {
      const cc = chargeCycleStatus(u);
      if (cc === "charging")    charging.push(u);
      else if (cc === "overdue") overdue.push(u);
      else if (cc === "due")     due.push(u);
      // sold / completed / ok / new are excluded.
    }
    const sortByAge = (a, b) => {
      const ad = daysSinceCheck(a) ?? (a.daysOnLot ?? 0);
      const bd = daysSinceCheck(b) ?? (b.daysOnLot ?? 0);
      return bd - ad;
    };
    charging.sort(sortByAge);
    overdue.sort(sortByAge);
    due.sort(sortByAge);
    return { charging, overdue, due };
  }, [visibleUnits]);
  const totalQueue = buckets.charging.length + buckets.overdue.length + buckets.due.length;
  const totalUnits = units.length;

  // Whether to render each bucket based on the active filter pill.
  const showCharging = filter === "all" || filter === "charging";
  const showOverdue  = filter === "all" || filter === "overdue";
  const showDue      = filter === "all" || filter === "due";

  async function quickAction(unit, action) {
    if (!writable) return;
    setPendingId(unit.id);
    try {
      await recordQuickCheck({
        unitId: unit.id, action, by: authorFromProfile(profile),
      });
      // Confirm via toast. Use a verb-matched message so the tech knows
      // exactly what they just logged.
      const msg = action === "Charge Complete" ? `${unit.stockNumber || "Unit"} — cycle reset`
                : action === "Charging"        ? `${unit.stockNumber || "Unit"} — charging started`
                : action === "Battery Replaced"? `${unit.stockNumber || "Unit"} — battery replaced`
                : `${unit.stockNumber || "Unit"} — ${action}`;
      toast.ok(msg);
    } catch (err) {
      toast.error(`Couldn't log: ${err.message || String(err)}`);
      setError(err);
    } finally {
      setPendingId(null);
    }
  }

  function toggleSelect(id) {
    setSelected((cur) => {
      const next = new Set(cur);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }
  function clearSelection() { setSelected(new Set()); }

  // Bulk-mark every selected unit as charged (cycle reset). Done in parallel
  // and toasted as one summary line. If anything fails, we still surface
  // partial success so techs aren't blocked.
  async function bulkChargeComplete() {
    if (!writable || selected.size === 0) return;
    const ids = Array.from(selected);
    setBulkPending(true);
    const by = authorFromProfile(profile);
    const results = await Promise.allSettled(
      ids.map((id) => recordQuickCheck({ unitId: id, action: "Charge Complete", by })),
    );
    setBulkPending(false);
    const ok = results.filter((r) => r.status === "fulfilled").length;
    const fail = results.length - ok;
    clearSelection();
    if (fail === 0) toast.ok(`${ok} unit${ok === 1 ? "" : "s"} marked charged — cycle reset`);
    else if (ok === 0) toast.error(`Couldn't mark ${fail} unit${fail === 1 ? "" : "s"} — try again`);
    else toast.warn(`${ok} charged, ${fail} failed — open them individually`);
  }

  return (
    <main className="container tight">
      <section className="page-hero">
        <div className="eyebrow">Lot walk · {profile?.storeId || "—"}</div>
        <h1>
          {totalQueue === 0 ? (
            <>The lot is <em style={{ color: "var(--ok)" }}>clean</em>.</>
          ) : (
            <>
              {totalQueue} unit{totalQueue === 1 ? "" : "s"}{" "}
              <em>need attention</em>.
            </>
          )}
        </h1>
        <p className="sub">
          {totalQueue === 0
            ? <>{totalUnits} total in inventory · all inside the {CHARGE_CYCLE.dueDays}-day cycle.</>
            : <>
                {buckets.charging.length > 0 && <>{buckets.charging.length} currently charging · </>}
                {buckets.overdue.length  > 0 && <><b className="crit">{buckets.overdue.length} overdue</b> · </>}
                {buckets.due.length      > 0 && <><b className="warn">{buckets.due.length} due this week</b> · </>}
                {totalUnits} total in inventory.
              </>
          }
        </p>
      </section>

      {error && (
        <div className="panel" style={{ marginTop: 8, borderColor: "var(--over)", color: "var(--over)" }}>
          {error.message || String(error)}
        </div>
      )}

      {/* Search + filter row. Inline so controls don't get buried under
          sticky chrome on mobile. */}
      <div className="lot-toolbar">
        <div className="lot-search" style={{ position: "relative", flex: "1 1 240px", minWidth: 0 }}>
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search stock # · model · customer"
            aria-label="Search inventory"
          />
          <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor"
               strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"
               style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", color: "var(--text-dim)", pointerEvents: "none" }}>
            <circle cx="11" cy="11" r="7" />
            <path d="M21 21l-4.3-4.3" />
          </svg>
          {search && (
            <button
              type="button"
              onClick={() => setSearch("")}
              aria-label="Clear search"
              style={{
                position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)",
                background: "transparent", border: 0, color: "var(--text-dim)", cursor: "pointer",
                padding: 4, lineHeight: 0,
              }}
            >
              <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <path d="M18 6L6 18M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>
      </div>

      <div className="lot-filters" style={{ margin: "0 0 18px" }}>
        <FilterChip active={filter === "all"}      onClick={() => setFilter("all")}      label={`All · ${totalQueue}`} tone="default" />
        <FilterChip active={filter === "overdue"}  onClick={() => setFilter("overdue")}  label={`Overdue · ${buckets.overdue.length}`}   tone="crit" />
        <FilterChip active={filter === "due"}      onClick={() => setFilter("due")}      label={`Due soon · ${buckets.due.length}`}      tone="warn" />
        <FilterChip active={filter === "charging"} onClick={() => setFilter("charging")} label={`Charging · ${buckets.charging.length}`} tone="hv" />
      </div>

      {showCharging && buckets.charging.length > 0 && (
        <UnitGroup
          title={`Charging now · ${buckets.charging.length}`}
          tone="ok"
          units={buckets.charging}
          navigate={navigate}
          writable={writable}
          pendingId={pendingId}
          onQuick={quickAction}
          selected={selected}
          onToggleSelect={toggleSelect}
        />
      )}

      {showOverdue && (
        <UnitGroup
          title={`Overdue · ${buckets.overdue.length}`}
          tone="crit"
          units={buckets.overdue}
          navigate={navigate}
          writable={writable}
          pendingId={pendingId}
          onQuick={quickAction}
          selected={selected}
          onToggleSelect={toggleSelect}
        />
      )}

      {showDue && (
        <UnitGroup
          title={`Due Soon · ${buckets.due.length}`}
          tone="warn"
          units={buckets.due}
          navigate={navigate}
          writable={writable}
          pendingId={pendingId}
          onQuick={quickAction}
          selected={selected}
          onToggleSelect={toggleSelect}
        />
      )}

      {/* No-match state — shown when search/filter yields zero. */}
      {totalQueue === 0 && (search || filter !== "all") && totalUnits > 0 && !error && (
        <div className="panel" style={{ marginTop: 14, color: "var(--text-muted)", textAlign: "center", padding: "28px 18px" }}>
          No matches for {search ? <>"<b style={{ color: "var(--text)" }}>{search}</b>"</> : "this filter"}.{" "}
          {(search || filter !== "all") && (
            <button
              type="button"
              onClick={() => { setSearch(""); setFilter("all"); }}
              style={{ background: "none", border: 0, color: "var(--brand-cobalt)", cursor: "pointer", padding: 0, font: "inherit", textDecoration: "underline" }}
            >
              Clear
            </button>
          )}
        </div>
      )}

      {totalQueue === 0 && totalUnits === 0 && !error && (
        <div className="panel" style={{ marginTop: 18, color: "var(--text-dim)", textAlign: "center" }}>
          No units yet. Import a DAR from the manager view to populate inventory.
        </div>
      )}

      {totalQueue === 0 && totalUnits > 0 && !error && (
        <div className="ok-collapsed" style={{ marginTop: 18 }}>
          <div>
            <strong>Nothing due today.</strong>{" "}
            All {totalUnits} unit{totalUnits === 1 ? "" : "s"} are inside the {CHARGE_CYCLE.dueDays}-day cycle.
          </div>
        </div>
      )}

      <div style={{ marginTop: 28, fontSize: 12.5, paddingBottom: selected.size > 0 ? 80 : 0 }}>
        <Link to="/today" className="nav-link">← Today</Link>
      </div>

      {/* Bulk action bar — fixed to viewport, appears only when something
          is selected. Two actions: Start charge (writes "Charging" action
          across the batch) and Mark charged (writes "Charge Complete"
          across the batch). Both surface a single toast summarizing the
          batch result. Tapping the count chip clears the selection. */}
      {selected.size > 0 && (
        <div role="region" aria-label="Bulk actions" className="bulk-bar">
          <button
            type="button"
            onClick={clearSelection}
            className="count-chip"
            aria-label={`${selected.size} selected — clear`}
          >
            {selected.size} selected
            <svg viewBox="0 0 24 24" width="11" height="11" fill="none" stroke="currentColor"
                 strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
          <button
            type="button"
            disabled={!writable || bulkPending}
            className="b-btn"
            onClick={async () => {
              if (!writable) return;
              const ids = Array.from(selected);
              setBulkPending(true);
              const by = authorFromProfile(profile);
              const results = await Promise.allSettled(
                ids.map((id) => recordQuickCheck({ unitId: id, action: "Charging", by })),
              );
              setBulkPending(false);
              const ok = results.filter((r) => r.status === "fulfilled").length;
              const fail = results.length - ok;
              clearSelection();
              if (fail === 0) toast.ok(`${ok} unit${ok === 1 ? "" : "s"} started charging`);
              else if (ok === 0) toast.error(`Couldn't start ${fail} unit${fail === 1 ? "" : "s"} — try again`);
              else toast.warn(`${ok} started, ${fail} failed`);
            }}
          >
            Start charge
          </button>
          <button
            type="button"
            disabled={!writable || bulkPending}
            onClick={bulkChargeComplete}
            className="b-btn primary"
          >
            <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor"
                 strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M20 6L9 17l-5-5" />
            </svg>
            {bulkPending ? "Logging…" : "Mark charged"}
          </button>
        </div>
      )}
    </main>
  );
}

// Filter chip — pill toggle, editorial palette.
function FilterChip({ active, onClick, label, tone }) {
  const cls = active ? `chip active ${tone === "crit" ? "crit" : tone === "warn" ? "warn" : tone === "hv" ? "hv" : ""}` : "chip";
  return (
    <button type="button" onClick={onClick} aria-pressed={active} className={cls}>
      {label}
    </button>
  );
}

// One bucket of units, with a colored divider header and the cards stacked.
function UnitGroup({ title, tone, units, navigate, writable, pendingId, onQuick, selected, onToggleSelect }) {
  if (!units || units.length === 0) return null;
  return (
    <>
      <div className={`q-divider ${tone}`}>{title}</div>
      {units.map((u) => (
        <UnitCard
          key={u.id}
          unit={u}
          tone={tone}
          navigate={navigate}
          writable={writable}
          pending={pendingId === u.id}
          onQuick={onQuick}
          isSelected={selected ? selected.has(u.id) : false}
          onToggleSelect={onToggleSelect}
        />
      ))}
    </>
  );
}

// Inline SVG icons. SEVRIC house style: 1.8 stroke, currentColor, fill:none,
// viewBox 0 0 24 24. Sized at the call site via width/height.
const Icon = {
  bolt: (props) => (
    <svg {...props} viewBox="0 0 24 24" fill="none" stroke="currentColor"
         strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M13 2L4 14h7l-1 8 9-12h-7l1-8z" />
    </svg>
  ),
  check: (props) => (
    <svg {...props} viewBox="0 0 24 24" fill="none" stroke="currentColor"
         strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M20 6L9 17l-5-5" />
    </svg>
  ),
  note: (props) => (
    <svg {...props} viewBox="0 0 24 24" fill="none" stroke="currentColor"
         strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M14 3H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z" />
      <path d="M14 3v6h6" />
      <path d="M9 13h6M9 17h4" />
    </svg>
  ),
  pin: (props) => (
    <svg {...props} viewBox="0 0 24 24" fill="none" stroke="currentColor"
         strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M12 21s-7-7.6-7-12a7 7 0 1 1 14 0c0 4.4-7 12-7 12z" />
      <circle cx="12" cy="9" r="2.5" />
    </svg>
  ),
};

function UnitCard({ unit, tone, navigate, writable, pending, onQuick, isSelected, onToggleSelect }) {
  const cc = chargeCycleStatus(unit);
  const ds = daysSinceCheck(unit);
  const isCharging = !!unit.charging;
  const className = `card-unit tilt-card ${
    cc === "overdue"  ? "crit" :
    cc === "due"      ? "warn" : "ok"
  }`;
  const heading = [unit.year, unit.make, unit.model, unit.trim].filter(Boolean).join(" ") || unit.stockNumber;
  const elapsed = elapsedCharging(unit);

  // The big lead number on the card. Time-based — days since the last
  // check, or days on lot if the unit has never been checked. Mono lockup
  // mirrors how the v2 SEVRIC card has always rendered its primary metric
  // (.voltage 44px monospace).
  const leadDays  = ds != null ? ds : (unit.daysOnLot ?? 0);
  const leadLabel = ds != null ? "since charge" : "on lot · new";

  async function editNote() {
    const next = window.prompt(
      `Sticky note for ${unit.stockNumber || "this unit"}\n(Customer pickup, keys location, do-not-move, etc.)`,
      unit.note || "",
    );
    if (next == null) return;
    try { await setStickyNote(unit.id, next.trim()); }
    catch (err) { window.alert("Couldn't save note: " + (err.message || err)); }
  }

  return (
    <ChargingHalo active={isCharging}>
    <div className={className} style={isSelected ? { boxShadow: "inset 3px 0 0 var(--accent)", borderColor: "var(--accent-line)" } : undefined}>
      <div className="stk-row" style={{ display: "flex", alignItems: "center", gap: 10 }}>
        {onToggleSelect && (
          <label
            onClick={(e) => e.stopPropagation()}
            className={`row-check${isSelected ? " checked" : ""}`}
            aria-label={`Select ${unit.stockNumber || "unit"}`}
          >
            <input
              type="checkbox"
              checked={!!isSelected}
              onChange={() => onToggleSelect(unit.id)}
            />
            {isSelected && (
              <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor"
                   strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                <path d="M20 6L9 17l-5-5" />
              </svg>
            )}
          </label>
        )}
        <span className="stk">{unit.stockNumber || "—"}</span>
        <span className="days" style={{ marginLeft: "auto" }}>{(unit.daysOnLot ?? 0)} days on lot</span>
      </div>
      <div className="model">{heading}</div>

      <div className="vrow">
        <span className="voltage">
          <LiveTick value={leadDays} precision={0} /><span className="unit">d</span>
        </span>
        {typeof unit.voltage === "number" && (
          <BatteryGlyph voltage={unit.voltage} width={44} height={20} showValue />
        )}
        {isCharging && (
          <span className="badge hv" style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
            <Icon.bolt width="11" height="11" />
            Charging{elapsed && ` · ${elapsed}`}
          </span>
        )}
        {!isCharging && cc === "overdue" && <span className="badge crit">Overdue</span>}
        {!isCharging && cc === "due"     && <span className="badge warn">Due soon</span>}
        <span className="badge muted" style={{ marginLeft: "auto" }}>{leadLabel}</span>
      </div>

      <div className="last" style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
        <span>
          Last action: {unit.lastAction || "none"}
          {unit.lastCheckAt && <> · {relativeTime(unit.lastCheckAt)}</>}
        </span>
        {unit.lotPos && (
          <span style={{ display: "inline-flex", alignItems: "center", gap: 4, color: "var(--text-dim)" }}>
            <Icon.pin width="12" height="12" />
            Lot {unit.lotPos}
          </span>
        )}
      </div>

      {unit.note && (
        <div
          onClick={editNote}
          style={{
            marginTop: 10, padding: "10px 12px",
            background: "var(--warn-soft)",
            border: "1px solid rgba(176,94,19,0.22)",
            borderRadius: 10,
            display: "flex", alignItems: "flex-start", gap: 10,
            fontSize: 13, lineHeight: 1.45, color: "var(--text-muted)",
            cursor: "pointer",
          }}
        >
          <Icon.note width="14" height="14" style={{ marginTop: 1, color: "var(--amber)", flexShrink: 0 }} />
          <span>{unit.note}</span>
        </div>
      )}

      <div className="card-actions">
        {isCharging ? (
          <button
            className="act primary"
            style={{ gridColumn: "1 / span 3", display: "inline-flex", gap: 8, alignItems: "center", justifyContent: "center" }}
            disabled={!writable || pending}
            onClick={() => onQuick(unit, "Charge Complete")}
            aria-label={`Mark ${unit.stockNumber || "unit"} as done charging`}
          >
            {pending ? "Logging…" : (<><Icon.check width="14" height="14" /> Done charging</>)}
          </button>
        ) : (
          <button
            className="act primary"
            style={{ gridColumn: "1 / span 3", display: "inline-flex", gap: 8, alignItems: "center", justifyContent: "center" }}
            disabled={!writable || pending}
            onClick={() => onQuick(unit, "Charging")}
            aria-label={`Start charging ${unit.stockNumber || "unit"}`}
          >
            {pending ? "Logging…" : (<><Icon.bolt width="14" height="14" /> Start charge</>)}
          </button>
        )}
        <button
          className="act more"
          onClick={editNote}
          style={{ display: "inline-flex", gap: 6, alignItems: "center", justifyContent: "center" }}
        >
          <Icon.note width="13" height="13" />
          {unit.note ? "Edit note" : "Add note"}
        </button>
        <button
          className="act more"
          onClick={() => navigate(`/check/${unit.id}`)}
        >
          More options
        </button>
      </div>
    </div>
    </ChargingHalo>
  );
}

